import { ProfileType } from '../types/enums/profileType'
import { Profile } from '../types/profile'
import { BasicDataParam, GraphDataParam, GraphDataParamString } from '../types/enums/graphDataParam'
import { EndCondition } from '../types/enums/endCondition'
import { Machine } from '../types/machine'
import { convertFromTempScale, convertToTempScale } from './graphUtils'
import { MachineSlot } from '../types/machineslot'
import { convertFromWeightScale, convertToWeightScale } from './weightUtils'
import { generateAbsoluteUrl } from './service'
import { ProfileEditorSeries, ProfileEditorSeriesParam } from '../types/profileEditorSeries'
import { SeriesType } from '../types/enums/seriesType'
import { GraphDatapoint } from '../types/graphSeriesConfig'

export const isBtBasedProfile = (profileType: ProfileType): boolean => {
  return profileType === ProfileType.BT_POWER || profileType === ProfileType.BT_INLET
}

export const getSeriesMarkerSymbol = (param: GraphDataParam): 'circle' | 'diamond' | 'square' => {
  if (param === BasicDataParam.RPM) {
    return 'square'
  } else if (param === BasicDataParam.FAN) {
    return 'diamond'
  }
  return 'circle'
}

const getDefaultValues = (profileType: ProfileType, defaultY: number): Array<Array<number>> => {
  return isBtBasedProfile(profileType)
    ? [
        [50, defaultY],
        [100, defaultY],
        [210, defaultY],
      ]
    : [
        [0, defaultY],
        [600000, defaultY],
      ]
}

export const getDefaultPowerBase = (profileType: ProfileType): Array<Array<number>> => {
  return getDefaultValues(profileType, 61)
}

export const getDefaultTempBase = (profileType: ProfileType): Array<Array<number>> => {
  return getDefaultValues(profileType, 200)
}

const getDefaultFanBase = (profileType: ProfileType): Array<Array<number>> => {
  return getDefaultValues(profileType, 75)
}

const getDefaultRpmBase = (profileType: ProfileType): Array<Array<number>> => {
  return getDefaultValues(profileType, 55)
}

export const getDefaultBbpProfileData = (profileType: ProfileType, defaultName: string): Profile => {
  return {
    ...getDefaultProfileData(profileType),
    name: defaultName,
    end_condition: EndCondition.RoastingTimeTarget,
    end_condition_value: 600000,
    is_bbp_profile: true,
  }
}

export const getDefaultProfileData = (profileType: ProfileType): Profile => {
  return {
    id: -1,
    url: '',
    name: '',
    profile_type: profileType,
    temperature_bezier: getDefaultTempBase(profileType),
    power_bezier: getDefaultPowerBase(profileType),
    rpm_bezier: getDefaultRpmBase(profileType),
    fan_bezier: getDefaultFanBase(profileType),
    runlength: 0,
    customer: '',
    author_name: '',
    modified: new Date(),
    created: new Date(),
    share_uuid: null,
    public_share_uuid: null,
    public_share_disabled: false,
    batch_weight: 0,
    preheat_temperature: 0,
    end_condition: EndCondition.None,
    end_condition_value: 0,
    parent: null,
    is_leaf: true,
    is_bbp_profile: false,
    notes: null,
    selected_filters: null,
  }
}

// all profile types also have fan and RPM series
const profileTypeConfig: { [key: string]: { mainProp: ProfileEditorSeriesParam; series: Array<GraphDataParam> } } = {
  [ProfileType.BT]: { mainProp: BasicDataParam.BT, series: [BasicDataParam.BT] },
  [ProfileType.ET]: { mainProp: BasicDataParam.ET, series: [BasicDataParam.ET] },
  [ProfileType.HYBRID_BT]: { mainProp: BasicDataParam.BT, series: [BasicDataParam.BT, BasicDataParam.POWER] },
  [ProfileType.HYBRID_ET]: { mainProp: BasicDataParam.ET, series: [BasicDataParam.ET, BasicDataParam.POWER] },
  [ProfileType.POWER]: { mainProp: BasicDataParam.POWER, series: [BasicDataParam.POWER] },
  [ProfileType.INLET_TEMP]: { mainProp: BasicDataParam.INLET, series: [BasicDataParam.INLET] },
  [ProfileType.BT_POWER]: { mainProp: BasicDataParam.POWER, series: [BasicDataParam.POWER] },
  [ProfileType.BT_INLET]: { mainProp: BasicDataParam.INLET, series: [BasicDataParam.INLET] },
}

export const getMainProfileParam = (profileType: ProfileType): ProfileEditorSeriesParam => {
  return profileTypeConfig[profileType].mainProp
}

const profileHasSeries = (param: GraphDataParam, profile: Profile): boolean => {
  if (param === BasicDataParam.RPM && !profile.is_bbp_profile) {
    return true
  }
  if (param === BasicDataParam.FAN) {
    return true
  }
  return profileTypeConfig[profile.profile_type].series.indexOf(param) > -1
}

export const createSlotPayload = (
  profile: Profile | null,
  slotIndex: number,
  machineUrl: string,
  existingUrl?: string
): MachineSlot => {
  return {
    url: existingUrl || '',
    slot_index: slotIndex,
    requested_profile: profile?.url || '',
    requested_profile_data: profile,
    machine: machineUrl,
    loaded_profile: '',
    loaded_timestamp: null,
  }
}

export const getXaxisLimits = (profileType: ProfileType, editorMaxLength: number): Array<number> => {
  // 250 degrees celsius is max on x axis if using bt/power profile
  const EDITOR_MAX_X_TEMP = 250
  return [
    isBtBasedProfile(profileType) ? convertToTempScale(50, 0) : 0,
    isBtBasedProfile(profileType) ? convertToTempScale(EDITOR_MAX_X_TEMP, 0) : editorMaxLength,
  ]
}

export const getProfileSeries = (profile: Profile): Array<ProfileEditorSeries> => {
  const seriesConfig: Array<{ param: ProfileEditorSeriesParam; seriesType: SeriesType; data: number[][] | null }> = [
    { param: BasicDataParam.BT, seriesType: SeriesType.TEMPERATURE, data: profile.temperature_bezier },
    { param: BasicDataParam.ET, seriesType: SeriesType.TEMPERATURE, data: profile.temperature_bezier },
    { param: BasicDataParam.INLET, seriesType: SeriesType.TEMPERATURE, data: profile.temperature_bezier },
    { param: BasicDataParam.POWER, seriesType: SeriesType.PERCENT, data: profile.power_bezier },
    { param: BasicDataParam.FAN, seriesType: SeriesType.PERCENT, data: profile.fan_bezier },
    { param: BasicDataParam.RPM, seriesType: SeriesType.RPM, data: profile.rpm_bezier },
  ]
  return seriesConfig
    .filter((x) => profileHasSeries(x.param, profile))
    .map((x) => {
      return {
        param: x.param as any,
        seriesType: x.seriesType,
        data: baseDataToSortedPoints(x.data || []),
      }
    })
}

export const getSeriesLabel = (param: GraphDataParam): string => {
  return GraphDataParamString[param]
}

export const isValidProfileTypeForMachine = (profile: Profile, machine: Machine | null): boolean => {
  const isValidProfileType =
    getValidProfileTypeForMachine(machine, profile.is_bbp_profile).indexOf(profile.profile_type) > -1
  // check if machine has valid end condition (in case this is a bbp profile):
  const hasValidEndCondition = profile.end_condition
    ? getValidEndConditionsForMachine(machine, profile.is_bbp_profile).indexOf(profile.end_condition) > -1
    : true
  return isValidProfileType && hasValidEndCondition
}

export const getValidProfileTypeForMachine = (machine: Machine | null, isBbpProfile: boolean): Array<ProfileType> => {
  if (isBbpProfile) {
    return [ProfileType.ET, ProfileType.POWER]
  }
  return [
    ProfileType.ET,
    ProfileType.BT,
    ...(machine?.sensor_config?.has_inlet ? [ProfileType.INLET_TEMP] : []),
    ProfileType.POWER,
    ...(machine?.sensor_config?.has_inlet ? [ProfileType.BT_INLET] : []),
    ProfileType.BT_POWER,
  ]
}

export const getValidEndConditionsForMachine = (machine: Machine | null, isBbp: boolean): Array<EndCondition> => {
  if (isBbp) {
    return [
      EndCondition.RoastingTimeTarget,
      ...(machine?.sensor_config?.has_drum ? [EndCondition.DrumTemperatureTarget] : []),
    ]
  }
  return [
    EndCondition.None,
    EndCondition.RoastingTimeTarget,
    EndCondition.DevelopmentTimeTarget,
    EndCondition.DevelopmentTimePercentage,
    EndCondition.BeanTemperatureTarget,
  ]
}

export const canDeletePoint = (series: GraphDataParam, index: number, length: number): boolean => {
  if (series === BasicDataParam.FAN || series === BasicDataParam.RPM) {
    return true
  }
  return index !== 0 && index !== length - 1
}

const isTempEndCondition = (endCondition: EndCondition): boolean => {
  return endCondition === EndCondition.BeanTemperatureTarget || endCondition === EndCondition.DrumTemperatureTarget
}

export const convertProfileToDisplayUnits = (profile: Profile): Profile => {
  return {
    ...profile,
    preheat_temperature: convertToTempScale(profile.preheat_temperature, 1),
    batch_weight: convertToWeightScale(profile.batch_weight),
    temperature_bezier: profile.temperature_bezier
      ? profile.temperature_bezier.map((x) => [x[0], convertToTempScale(x[1], 1)])
      : profile.temperature_bezier,
    end_condition_value: isTempEndCondition(profile.end_condition)
      ? convertToTempScale(profile.end_condition_value, 1)
      : profile.end_condition_value,
    ...(profile.profile_type === ProfileType.BT_POWER && {
      power_bezier: profile.power_bezier?.map((x) => [convertToTempScale(x[0], 1), x[1]]),
      fan_bezier: profile.fan_bezier?.map((x) => [convertToTempScale(x[0], 1), x[1]]),
      rpm_bezier: profile.rpm_bezier?.map((x) => [convertToTempScale(x[0], 1), x[1]]),
    }),
    ...(profile.profile_type === ProfileType.BT_INLET && {
      temperature_bezier: profile.temperature_bezier?.map((x) => [
        convertToTempScale(x[0], 1),
        convertToTempScale(x[1], 1),
      ]),
      fan_bezier: profile.fan_bezier?.map((x) => [convertToTempScale(x[0], 1), x[1]]),
      rpm_bezier: profile.rpm_bezier?.map((x) => [convertToTempScale(x[0], 1), x[1]]),
    }),
  }
}

export const convertProfileToSaveUnits = (profileToConvert: Profile): Profile => {
  return {
    ...profileToConvert,
    id: profileToConvert.id === -1 ? 0 : profileToConvert.id,
    preheat_temperature: convertFromTempScale(profileToConvert.preheat_temperature),
    batch_weight: convertFromWeightScale(profileToConvert.batch_weight),
    ...(profileToConvert.temperature_bezier && {
      temperature_bezier: profileToConvert.temperature_bezier.map((x) => [x[0], convertFromTempScale(x[1])]),
    }),
    ...(profileToConvert.profile_type === ProfileType.BT_POWER && {
      power_bezier: (profileToConvert.power_bezier || []).map((x) => [convertFromTempScale(x[0]), x[1]]),
      rpm_bezier: (profileToConvert.rpm_bezier || []).map((x) => [convertFromTempScale(x[0]), x[1]]),
      fan_bezier: (profileToConvert.fan_bezier || []).map((x) => [convertFromTempScale(x[0]), x[1]]),
    }),
    ...(profileToConvert.profile_type === ProfileType.BT_INLET && {
      temperature_bezier: (profileToConvert.temperature_bezier || []).map((x) => [
        convertFromTempScale(x[0]),
        convertFromTempScale(x[1]),
      ]),
      rpm_bezier: (profileToConvert.rpm_bezier || []).map((x) => [convertFromTempScale(x[0]), x[1]]),
      fan_bezier: (profileToConvert.fan_bezier || []).map((x) => [convertFromTempScale(x[0]), x[1]]),
    }),
    end_condition_value: isTempEndCondition(profileToConvert.end_condition)
      ? convertFromTempScale(profileToConvert.end_condition_value)
      : profileToConvert.end_condition_value,
  }
}

export const getNextFreeSlotIndex = (
  machine: Machine | null,
  machineSlots: Array<MachineSlot>,
  selectedProfiles?: Array<Profile>
): number | undefined => {
  const machineMaxSlots = machine?.profile_slot_count || 0
  let nextFreeSlot: number | undefined = undefined
  const assignedSlots = machineSlots.filter((x) => !!x.requested_profile).map((x) => x.slot_index)
  for (let i = 0; i < machineMaxSlots; i++) {
    if (assignedSlots.indexOf(i) === -1) {
      if (!selectedProfiles) {
        nextFreeSlot = i
        break
      } else if (!selectedProfiles.some((p) => p.machine_slot_index === i)) {
        nextFreeSlot = i
        break
      }
    }
  }
  return nextFreeSlot
}

export const generateShareUrl = (shareUuid: string): string => {
  return generateAbsoluteUrl(`/shared_profile/${shareUuid}/`)
}

export const isPublicShareEnabled = (profile: Profile | null): boolean => {
  return !!profile && !!profile.public_share_uuid && !profile.public_share_disabled
}

export const isSharedProfile = (profile: Profile | null): boolean => {
  return !!profile && (!!profile.share_uuid || !!profile.public_share_uuid)
}

export const sortPoints = (points: Array<GraphDatapoint>): Array<GraphDatapoint> => {
  const dataToSort = [...points]
  dataToSort.sort((a, b) => {
    return a.msec - b.msec
  })
  return dataToSort
}

export const baseDataToSortedPoints = (baseData: number[][]): Array<GraphDatapoint> => {
  return sortPoints(
    baseData.map((x) => {
      return { msec: x[0], dataValue: x[1] }
    })
  )
}

export const movePointsLeft = (
  currentPoints: Array<GraphDatapoint>,
  selectedPointIndexes: Array<number>,
  moveInterval: number,
  adjustFn: (startValue: number) => number
): Array<GraphDatapoint> => {
  const updatedValues = currentPoints.length === 0 ? [] : [currentPoints[0]]
  for (let i = 1; i < currentPoints.length; i++) {
    const x = currentPoints[i]
    const minValue = updatedValues[updatedValues.length - 1].msec
    let newX = adjustFn(x.msec)
    if (newX <= minValue) {
      newX = minValue + moveInterval
    }
    const newValue = selectedPointIndexes.indexOf(i) > -1 ? { ...x, msec: newX } : x
    updatedValues.push(newValue)
  }
  return updatedValues
}

export const movePointsRight = (
  currentPoints: Array<GraphDatapoint>,
  selectedPointIndexes: Array<number>,
  moveInterval: number,
  maxTime: number,
  adjustFn: (startValue: number) => number
): Array<GraphDatapoint> => {
  const updatedValues = []
  for (let i = currentPoints.length - 1; i >= 0; i--) {
    const x = currentPoints[i]
    const maxValue = i === currentPoints.length - 1 ? maxTime : updatedValues[updatedValues.length - 1].msec
    let newX = i === 0 ? x.msec : adjustFn(x.msec)
    if (newX >= maxValue) {
      newX = maxValue - moveInterval
    }
    const newValue = selectedPointIndexes.indexOf(i) > -1 ? { ...x, msec: newX } : x
    updatedValues.push(newValue)
  }
  return updatedValues.reverse()
}

export const movePointsUp = (
  currentPoints: Array<GraphDatapoint>,
  selectedPointIndexes: Array<number>,
  maxY: number,
  adjustFn: (startValue: number) => number
): Array<GraphDatapoint> => {
  const updatedValues = currentPoints.map((x, index) => {
    let newY = adjustFn(x.dataValue || 0)
    if (newY > maxY) {
      newY = maxY
    }
    return selectedPointIndexes.indexOf(index) > -1 ? { ...x, dataValue: newY } : x
  })
  return updatedValues
}

export const movePointsDown = (
  currentPoints: Array<GraphDatapoint>,
  selectedPointIndexes: Array<number>,
  minY: number,
  adjustFn: (startValue: number) => number
): Array<GraphDatapoint> => {
  const updatedValues = currentPoints.map((x, index) => {
    let newY = adjustFn(x.dataValue || 0)
    if (newY < minY) {
      newY = minY
    }
    return selectedPointIndexes.indexOf(index) > -1 ? { ...x, dataValue: newY } : x
  })
  return updatedValues
}
